import { ILuckyDrawCreator, IReadingChallenge, IReadingChallengeCreator, ISuccessResult, IUserSegment } from '../../services/types';
import React, { useEffect, useRef, useState } from 'react';
import useStyles from './styles';
import useBackdrop from '../../hooks/useBackdrop';
import useSnackbar from '../../hooks/useSnackbar';
import api from '../../services/api/base';
import { Box, Grid, MenuItem, Paper, Select, TextField, Typography } from '@material-ui/core';
import { SubmitControl } from '../../components/SubmitControl';
import moment, { Moment } from 'moment-timezone';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { Canceler } from 'axios';
import { getSimpleUserSegments } from '../../services/api/Segment';
import { getReadingChallenge, editReadingChallenge, saveReadingChallenge } from '../../services/api/ReadingChallenge';

interface IReadingChallengeViewProps {
  readingChallengeId?: number;
  onCreated?: (data: IReadingChallengeCreator) => void;
  onError?: (error: Error) => void;
}

const ReadingChallengeView = (props: IReadingChallengeViewProps): JSX.Element => {
  const { readingChallengeId, onCreated, onError } = props;
  const classes = useStyles();

  const [segments, setSegments] = useState<IUserSegment[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState<number>();
  const [selectedStoryId, setSelectedStoryId] = useState<number>();
  const [rewardNum, setRewardNum] = useState<number>(0);
  const [startAt, setStartAt] = useState<Moment>(moment());
  const [endAt, setEndAt] = useState<Moment>(moment());
  const [internalTitle, setInternalTitle] = useState<string>('');
  const [internalDescription, setInternalDescription] = useState<string>('');
  const [storySummary, setStorySummary] = useState<string>('');

  const [currentReadingChallengeId, setCurrentReadingChallengeId] = useState<number>();
  const [pendingInitialLoading, setPendingInitialLoading] = useState<boolean>(false);

  const backdrop = useBackdrop();
  const snackbar = useSnackbar();

  const readingChallengeCanceler = useRef<Canceler>(() => undefined);
  const segmentsCanceler = useRef<Canceler>(() => undefined);

  const originalData = useRef<Partial<IReadingChallengeCreator>>({
    storyId: 0,
    segmentId: 0,
    rewardNum: 0,
    startAt: new Date(),
    endAt: new Date(),
    internalTitle: '',
    internalDescription: '',
    storySummary: '',
  });

  const fetchUserSegments = async () => {
    segmentsCanceler.current();

    try {
      const [fetchPromise, cancel] = getSimpleUserSegments<IUserSegment>(true);
      segmentsCanceler.current = cancel;
      const result = await fetchPromise;

      if (result && result.status === 200 && result.data) {
        const { items } = result.data;
        setSegments(items);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  useEffect(() => {
    fetchUserSegments();

    return () => segmentsCanceler.current();
  }, []);

  const setStatesFromData = (data: Partial<IReadingChallengeCreator>) => {
    data.storyId !== undefined && setSelectedStoryId(data?.storyId);
    data.segmentId !== undefined && setSelectedSegmentId(data?.segmentId);
    data.rewardNum !== undefined && setRewardNum(data?.rewardNum);
    data.startAt !== undefined && setStartAt(moment.utc(data?.startAt));
    data.endAt !== undefined && setEndAt(moment.utc(data?.endAt));
    data.internalTitle !== undefined && setInternalTitle(data?.internalTitle);
    data.internalDescription !== undefined && setInternalDescription(data?.internalDescription);
    data.storySummary !== undefined && setStorySummary(data?.storySummary);

    setCurrentReadingChallengeId(data.id);
  };

  useEffect(() => {
    if (!readingChallengeId) {
      return;
    }

    const fetchData = async () => {
      readingChallengeCanceler.current();

      try {
        setPendingInitialLoading(true);
        const [fetchPromise, cancel] = getReadingChallenge(readingChallengeId);
        readingChallengeCanceler.current = cancel;
        const result = await fetchPromise;

        if (result.status === 200) {
          const data = result.data;
          originalData.current = data;
          setStatesFromData(data);
        }
      } catch (e) {
        if (api.isCancel(e)) return;

        console.error('e', e);

        if (onError) {
          onError(e);
        }
      } finally {
        setPendingInitialLoading(false);
      }
    };

    fetchData();

    return () => readingChallengeCanceler.current();
  }, [onError, readingChallengeId]);

  const stateToPayload = (): Partial<IReadingChallengeCreator> => {
    return {
      segmentId: selectedSegmentId,
      storyId: selectedStoryId,
      rewardNum,
      startAt: startAt?.toDate(),
      endAt: endAt?.toDate(),
      internalTitle,
      internalDescription,
      storySummary,
    };
  };

  const handleClickSubmit = async () => {
    if (rewardNum <= 0) {
      snackbar.error(`reward number must be larger than 0`);
      return;
    }

    const payload: Partial<ILuckyDrawCreator> = stateToPayload();
    try {
      backdrop.show();

      const result = currentReadingChallengeId ? await editReadingChallenge(currentReadingChallengeId, payload) : await saveReadingChallenge(payload);
      if (api.isResponseSuccess(result)) {
        if (originalData.current) {
          setStatesFromData(originalData.current);
        }
        if (onCreated) {
          onCreated((result.data as ISuccessResult<IReadingChallenge>).data);
        }
      }
    } catch (e) {
      console.error(e);
      if (onError) {
        onError(e);
      }
    } finally {
      backdrop.hide();
    }
    return;
  };

  const isReadonly = !!currentReadingChallengeId;

  if (pendingInitialLoading) {
    return <div />;
  }

  return (
    <div className={classes.createReadingChallengeWrap}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        style={{ width: '100%', marginTop: '10px', marginBottom: '16px', paddingLeft: '4px', paddingRight: '22px' }}
      >
        <Typography variant="h6" component="h3" style={{ flex: 1 }}>
          {!currentReadingChallengeId ? 'Create Reading Challenge' : <div>ReadingChallenge: {originalData.current?.id}</div>}
        </Typography>
        {(!currentReadingChallengeId || (currentReadingChallengeId && moment(startAt).isAfter(moment().add(1, 'hour')))) && (
          <SubmitControl
            mode={'create'}
            onSubmitClick={() => {
              handleClickSubmit();
            }}
          />
        )}
      </Box>

      <Grid container direction="row" justify="flex-start">
        <Grid item xs={6}>
          <Paper className={classes.contentSettingWrap}>
            <Typography variant="h6" component="h3" className={classes.sectionTitle}>
              Reading Challenge Setting
            </Typography>

            <Grid container direction="column" justify="flex-start" alignItems="stretch">
              <Box paddingY={2} width="100%">
                {currentReadingChallengeId && (
                  <Grid item xs={12}>
                    <TextField className={classes.infoTextField} value={currentReadingChallengeId} disabled helperText={'Reading Challenge Id'} />
                  </Grid>
                )}

                <Grid container>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Segment
                  </Typography>

                  <Grid item xs={12}>
                    <Select
                      displayEmpty
                      fullWidth
                      required
                      value={selectedSegmentId || ''}
                      disabled={isReadonly || segments.length === 0}
                      onChange={e => setSelectedSegmentId(e.target.value as number)}
                      className={classes.radioButtonGroups}
                    >
                      {segments.map((sg: IUserSegment) => (
                        <MenuItem key={sg.id} value={sg.id}>
                          {sg.id} - {sg.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Story Id"
                    className={classes.infoTextField}
                    value={selectedStoryId || ''}
                    required
                    disabled={isReadonly}
                    onChange={e => {
                      if (isReadonly) {
                        return;
                      }

                      setSelectedStoryId(Number(e.target.value));
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: {
                        root: classes.inputLabel,
                      },
                    }}
                    InputProps={{
                      classes: {
                        input: classes.tfInput,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="# of reward coupons"
                    className={classes.infoTextField}
                    value={rewardNum || ''}
                    required
                    disabled={isReadonly}
                    onChange={e => {
                      if (isReadonly) {
                        return;
                      }

                      setRewardNum(Number(e.target.value));
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: {
                        root: classes.inputLabel,
                      },
                    }}
                    InputProps={{
                      classes: {
                        input: classes.tfInput,
                      },
                    }}
                  />
                </Grid>

                <Grid container alignItems="stretch">
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Event starts at
                  </Typography>

                  <Grid item xs={12}>
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                      <KeyboardDateTimePicker
                        format="llll"
                        required
                        margin="normal"
                        id="date-picker-inline"
                        disabled={isReadonly}
                        value={startAt.format()}
                        minDate={moment()
                          .add(1, 'hour')
                          .toDate()}
                        minDateMessage=""
                        onChange={data => {
                          if (data) setStartAt(data);
                        }}
                        className={classes.radioButtonGroups}
                      />
                    </MuiPickersUtilsProvider>
                  </Grid>
                </Grid>

                <Grid container alignItems="stretch">
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Event ends at
                  </Typography>

                  <Grid item xs={12}>
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                      <KeyboardDateTimePicker
                        format="llll"
                        required
                        margin="normal"
                        id="date-picker-inline"
                        disabled={isReadonly}
                        value={endAt.format()}
                        onChange={data => {
                          if (data) setEndAt(data);
                        }}
                        className={classes.radioButtonGroups}
                      />
                    </MuiPickersUtilsProvider>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={6}>
          <Paper className={classes.contentSettingWrap}>
            <Grid container direction="column" justify="flex-start" alignItems="stretch">
              <Box paddingY={2} width="100%">
                <Grid item xs={12}>
                  <TextField
                    label="Internal Title"
                    className={classes.infoTextField}
                    value={internalTitle || ''}
                    required
                    disabled={isReadonly}
                    onChange={e => {
                      if (isReadonly) {
                        return;
                      }

                      setInternalTitle(e.target.value);
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: {
                        root: classes.inputLabel,
                      },
                    }}
                    InputProps={{
                      classes: {
                        input: classes.tfInput,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Internal Description"
                    className={classes.infoTextField}
                    value={internalDescription || ''}
                    required
                    multiline
                    rows={4}
                    variant="outlined"
                    disabled={isReadonly}
                    onChange={e => {
                      if (isReadonly) {
                        return;
                      }

                      setInternalDescription(e.target.value);
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: {
                        root: classes.inputLabel,
                      },
                    }}
                    InputProps={{
                      classes: {
                        input: classes.tfInput,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Story Summary"
                    className={classes.infoTextField}
                    value={storySummary || ''}
                    required
                    multiline
                    rows={4}
                    variant="outlined"
                    disabled={isReadonly}
                    onChange={e => {
                      if (isReadonly) {
                        return;
                      }

                      setStorySummary(e.target.value);
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: {
                        root: classes.inputLabel,
                      },
                    }}
                    InputProps={{
                      classes: {
                        input: classes.tfInput,
                      },
                    }}
                  />
                </Grid>
              </Box>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default ReadingChallengeView;
